import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowRight } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { authService } from '../services/authService';

const SendResetPasswordEmail = () => {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        
        try {
            await authService.sendResetPasswordEmail(email);
            setSuccess(true);
        } catch (error: any) {
            toast.error(error.message || 'Failed to send reset email');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center p-4">
            <div className="w-full max-w-md">
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-2xl mb-4">
                        <img src="/apple-touch-icon.png" alt="CareLMS Logo" width={64} height={64} className="rounded-xl" />
                    </div>
                    <h1 className="text-3xl font-bold text-white mb-2">CareLMS</h1>
                    <p className="text-blue-100">Reset your password to regain access</p>
                </div>

                <div className="bg-white rounded-xl shadow-lg p-6">
                    <div className="text-center mb-6">
                        <h2 className="text-xl font-semibold text-black mb-2">Forgot Password</h2>
                        <p className="text-slate-600 text-sm">Enter your email to receive a reset link</p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <label htmlFor="email" className="block text-sm font-medium text-slate-700">
                                Email Address
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <Mail className="h-4 w-4 text-slate-400" />
                                </div>
                                <input
                                    id="email"
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                                    placeholder="Enter your email"
                                    required
                                    disabled={success}
                                />
                            </div>
                        </div>

                        <button
                            type="submit"
                            disabled={loading || success}
                            className={`w-full flex items-center justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 ${
                                (loading || success) ? 'opacity-50 cursor-not-allowed' : ''
                            }`}
                        >
                            {loading ? 'Sending...' : success ? 'Reset Link Sent' : 'Send Reset Link'}
                            <ArrowRight className="ml-2 h-4 w-4" />
                        </button>

                        {success && (
                            <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                                <p className="text-green-600 text-sm">
                                    Password reset link sent! Check your email for instructions.
                                </p>
                            </div>
                        )}
                    </form>

                    <div className="mt-6 text-center">
                        <span className="text-sm text-slate-600">
                            Remember your password?{' '}
                            <Link to="/login" className="font-medium text-blue-600 hover:text-blue-500 transition-colors">
                                Sign in
                            </Link>
                        </span>
                    </div>
                </div>

                <div className="text-center mt-6">
                    <p className="text-blue-100 text-sm">Built for Care. Trusted by Care Businesses.</p>
                </div>
            </div>
        </div>
    );
};

export default SendResetPasswordEmail; 